import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, User, Truck, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { type } = useParams();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    taxNumber: '',
    address: '',
    city: '',
    username: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [errors, setErrors] = useState({});

  const isToptanci = type === 'toptanci';

  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData({
      ...formData,
      [name]: inputType === 'checkbox' ? checked : value
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Ad Soyad gerekli';
    if (!formData.email) newErrors.email = 'E-posta gerekli';
    if (!formData.phone) newErrors.phone = 'Telefon gerekli';
    if (!formData.company) newErrors.company = 'Firma adı gerekli';
    if (isToptanci && !formData.taxNumber) newErrors.taxNumber = 'Vergi numarası gerekli';
    if (!formData.username) newErrors.username = 'Kullanıcı adı gerekli';
    if (!formData.password) newErrors.password = 'Şifre gerekli';
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }
    if (!formData.agreeTerms) newErrors.agreeTerms = 'Şartları kabul etmelisiniz';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      const result = await register({
        email: formData.email,
        username: formData.username,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        company: formData.company,
        role: isToptanci ? 'toptanci' : 'bireysel',
        tax_number: formData.taxNumber,
        address: formData.address,
        city: formData.city
      });
      setIsLoading(false);
      
      if (result.success) {
        setIsSubmitted(true);
      } else {
        alert(result.message);
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md backdrop-blur-xl bg-white/95 shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-20 h-20 mx-auto mb-4 text-green-500" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Başvurunuz Alındı!</h2>
            <p className="text-gray-600 mb-6">
              Üyelik başvurunuz başarıyla alınmıştır. Admin onayından sonra hesabınız aktif olacaktır.
              Size e-posta ile bilgi verilecektir.
            </p>
            <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700">
              Giriş Sayfasına Dön
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-white hover:text-blue-200 mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Giriş Sayfasına Dön
        </Link>

        <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center justify-center mb-4">
              {isToptanci ? (
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <Truck className="w-8 h-8 text-amber-600" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
              )}
            </div>
            <CardTitle className="text-2xl text-center text-gray-800">
              {isToptanci ? 'Toptancı Üyelik Başvurusu' : 'Bireysel Üyelik Başvurusu'}
            </CardTitle>
            <CardDescription className="text-center">
              Tüm alanları eksiksiz doldurun
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Soyad *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Adınız Soyadınız"
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ornek@email.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0532 123 4567"
                    className={errors.phone ? 'border-red-500' : ''}
                  />
                  {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Firma Adı *</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Firma Adınız"
                    className={errors.company ? 'border-red-500' : ''}
                  />
                  {errors.company && <p className="text-xs text-red-500">{errors.company}</p>}
                </div>
              </div>

              {isToptanci && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="taxNumber">Vergi Numarası *</Label>
                    <Input
                      id="taxNumber"
                      name="taxNumber"
                      value={formData.taxNumber}
                      onChange={handleInputChange}
                      placeholder="1234567890"
                      className={errors.taxNumber ? 'border-red-500' : ''}
                    />
                    {errors.taxNumber && <p className="text-xs text-red-500">{errors.taxNumber}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Şehir</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="İstanbul"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="address">Adres</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Teslimat adresi"
                />
              </div>

              <hr className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Kullanıcı Adı *</Label>
                  <Input
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="kullaniciadi"
                    className={errors.username ? 'border-red-500' : ''}
                  />
                  {errors.username && <p className="text-xs text-red-500">{errors.username}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Şifre *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Şifre Tekrar *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
              </div>

              <div className="flex items-start space-x-2 mt-4">
                <Checkbox
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onCheckedChange={(checked) => setFormData({ ...formData, agreeTerms: checked })}
                />
                <Label htmlFor="agreeTerms" className="text-sm leading-none cursor-pointer">
                  <span className={errors.agreeTerms ? 'text-red-500' : ''}>
                    Kullanım şartlarını ve gizlilik politikasını okudum, kabul ediyorum.
                  </span>
                </Label>
              </div>

              <Alert className="bg-blue-50 border-blue-200 mt-4">
                <AlertDescription className="text-blue-800 text-sm">
                  Üyelik başvurunuz admin onayı sonrasında aktif olacaktır. Onay durumu hakkında e-posta ile bilgilendirileceksiniz.
                </AlertDescription>
              </Alert>

              <Button 
                type="submit" 
                className={`w-full h-12 mt-6 ${isToptanci ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                Başvuruyu Gönder
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
